import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { withStyles, Button, CircularProgress, Dialog, Paper } from '@material-ui/core';
import { snackActions } from '../../../sc-utilities/SnackBar';
import PresetGeneralSettings from './PresetGeneralSettings';
import { CancelToken } from 'axios';
import * as actions from '../../../sc-customer/Customer/store/customerActions';
import { securazeInitialValues } from '../index';
import AppLogoLoading from '../../../sc-logo';

const styles = (theme) => ({
    root: {
        height: 'auto',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    paper: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    paperForm: {
        width: 'auto',
        height: 'auto',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        paddingTop: theme.spacing(2),
        marginLeft: theme.spacing(0),
        marginRight: theme.spacing(0),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(1),
    },
    submit: {
        width: '100%',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default,
        },
    },
    text: {
        borderRadius: '1px',
        marginRight: 10,
        color: theme.palette.buttonText.default,
    },
});

const GeneralPresets = (props) => {
    const {
        classes,
        activeCustomerId,
        translations,
        updatePresets,
        loadPresets,
        idParams,
        fetchOrders,
        fetchCollies,
        fetchGrades,
        fetchPrinters,
        customer,
        onLoadCustomer,
        customerUpdatePresets,
    } = props;

    const [loading, setLoading] = useState(true);

    const [customerPresetFilter, setCustomerPresetsFilter] = useState({
        orders: [],
        grades: [],
        printers: [],
        collies: [],
    });

    let presets = securazeInitialValues.Normal;
    if (customer?.Presets) {
        const customerPresets = JSON.parse(customer.Presets);
        if (customerPresets?.Normal) {
            presets = customerPresets.Normal;
        }
    }

    const initialValues = {
        PresetsEnabled: presets?.PresetsEnabled ?? false,
        OrderID: presets?.OrderID || '',
        TransportColliID: presets?.TransportColliID || '',
        ColliID: presets?.ColliID || '',
        AutoStartErasure: presets?.AutoStartErasure ?? false,
        AutoEnableRemoteAccess: presets?.AutoEnableRemoteAccess ?? true,
        AutoStartDiagnose: presets?.AutoStartDiagnose ?? false,
        PrinterID: presets?.PrinterID || '',
        AutoGradingFromTypicalProblems: presets?.AutoGradingFromTypicalProblems ?? false,
        GradeID: presets?.GradeID || '',
        ProcessingSessionEndMessage: presets?.ProcessingSessionEndMessage ?? '',
        DebugMode: presets?.DebugMode ?? '0',
    };

    const customerCancel = CancelToken?.source();
    const ordersCancel = CancelToken?.source();
    const colliesCancel = CancelToken?.source();
    const gradesCancel = CancelToken.source();
    const printersCancel = CancelToken.source();

    const collectPresets = (data) => {
        const LooseDrives = {
            PresetsEnabled: !!data?.PresetsEnabled,
            AutoEnableRemoteAccess: !!data?.AutoEnableRemoteAccess,
            AutoStartErasure: !!data?.AutoStartErasure,
            OrderID: data?.OrderID,
            TransportColliID: data?.TransportColliID,
            ColliID: data?.ColliID,
            PrinterID: data?.PrinterID,
            AutoGradingFromTypicalProblems: !!data?.AutoGradingFromTypicalProblems,
            GradeID: data?.GradeID,
            ProcessingSessionEndMessage: data?.ProcessingSessionEndMessage,
            DebugMode: data?.DebugMode,
        };

        const presetData = {
            Normal: {
                ...LooseDrives,
                AutoStartDiagnose: !!data?.AutoStartDiagnose,
            },
            LooseDrives,
            ...LooseDrives,
            AutoStartDiagnose: !!data?.AutoStartDiagnose,
        };
        return presetData;
    };

    const handleLoadPresets = () => {
        loadPresets
            ? loadPresets()
            : onLoadCustomer(
                  { CustomerID: activeCustomerId, ActiveCustomerID: activeCustomerId },
                  { cancelToken: customerCancel }
              );
    };

    const handleUpdatePresets = async (presets) => {
        return (await updatePresets)
            ? updatePresets({ Presets: presets, ...idParams, ActiveCustomerID: activeCustomerId })
            : customerUpdatePresets({
                  Presets: presets,
                  CustomerID: customer?.CustomerID,
                  ActiveCustomerID: activeCustomerId,
              });
    };

    useEffect(() => {
        const fetchData = async () => {
            let orderData = await fetchOrders(
                {
                    ActiveCustomerID: activeCustomerId,
                    q: { Status: ['new', 'scheduled', 'in_warehouse', 'on_hold'] },
                    ...idParams,
                },
                { cancelToken: ordersCancel }
            );
            let collies = await fetchCollies(
                {
                    ActiveCustomerID: activeCustomerId,
                    q: { Status: [null, 'Active'], Locked: false, Finished: ['0'] },
                    ...idParams,
                },
                { cancelToken: colliesCancel }
            );
            let grades = await fetchGrades(
                { ActiveCustomerID: activeCustomerId, ...idParams },
                { cancelToken: gradesCancel }
            );
            let printers = await fetchPrinters(
                { ActiveCustomerID: activeCustomerId, ...idParams },
                { cancelToken: printersCancel }
            );

            if (orderData && collies && grades && printers) {
                setCustomerPresetsFilter({
                    orders: orderData,
                    collies: collies,
                    printers: [{ value: null, displayValue: translations['none'] || 'None' }, ...printers],
                    grades: [{ value: null, displayValue: translations['none'] || 'None' }, ...grades],
                });
            }
            setLoading(false);
        };

        handleLoadPresets();

        fetchData();

        return () => {
            customerCancel.cancel('Operation canceled by user');
            ordersCancel.cancel('Operation canceled by the user.');
            colliesCancel.cancel('Operation canceled by the user.');
            gradesCancel.cancel('Operation canceled by the user.');
            printersCancel.cancel('Operation canceled by the user.');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCustomerId]);

    let formSettings = {
        Validation: {},
        ButtonSubmit: {
            Title: translations['save'] || 'SAVE',
            Color: 'primary',
        },
    };

    const handleSubmit = async (data) => {
        const presets = collectPresets(data);

        const serverResult = await handleUpdatePresets(presets);
        handleLoadPresets();

        setLoading(false);
        if (serverResult.status) {
            return snackActions.snackBarComponent(translations['update_presets'] || 'Presets updated.', 'success');
        } else {
            return snackActions.snackBarComponent(
                translations['failed_to_update_presets'] || 'Error: Failed to update presets',
                'error'
            );
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                open={loading}
                PaperProps={{
                    classes: {
                        root: classes.paper,
                    },
                }}
            >
                <AppLogoLoading />
            </Dialog>
            <Paper className={classes.paperForm}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }}
                    enableReinitialize
                    validationSchema={Yup.object().shape(formSettings.Validation)}
                >
                    {(formikProps) => {
                        const { values, isSubmitting, setFieldValue, handleChange, isValid, handleSubmit, resetForm } =
                            formikProps;

                        const generalProps = {
                            translations: translations,
                            handleChange: handleChange,
                            values: values,
                            orders: customerPresetFilter.orders,
                            collies: customerPresetFilter.collies,
                            setFieldValue: setFieldValue,
                            grades: customerPresetFilter.grades,
                            printers: customerPresetFilter.printers,
                        };

                        return (
                            <form
                                className={classes.form}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }}
                            >
                                <Button
                                    className={classes.text}
                                    variant="contained"
                                    color={'primary'}
                                    onClick={async () => {
                                        let serverResult;
                                        resetForm();
                                        serverResult = await handleUpdatePresets({
                                            ...securazeInitialValues,
                                            ...idParams,
                                        });
                                        if (serverResult) {
                                            handleLoadPresets();
                                            return setLoading(false);
                                        }
                                        if (serverResult?.status) {
                                            return snackActions.snackBarComponent('Presets updated.', 'success');
                                        } else {
                                            return snackActions.snackBarComponent(
                                                'Error: Failed to update presets',
                                                'error'
                                            );
                                        }
                                    }}
                                >
                                    {translations['reset'] || 'Reset'}
                                </Button>

                                <PresetGeneralSettings {...generalProps} />

                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            disabled={!isValid || isSubmitting || loading}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                formSettings.ButtonSubmit.Title
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Paper>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomerId: state?.core?.login?.activeCustomer?.CustomerID || null,
        customer: state?.customer?.customers?.loaded || null,
        permissions: state?.core?.login?.permissions || [],
        translations: state?.translate?.translations?.activeTranslations || {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onLoadCustomer: (customerID, options) => dispatch(actions.loadCustomer(customerID, options)),
        customerUpdatePresets: (params, options) => dispatch(actions.updatePresets(params, options)),
        fetchOrders: (id, options) => dispatch(actions.fetchOrders(id, options)),
        fetchCollies: (id, options) => dispatch(actions.fetchCollies(id, options)),
        fetchPrinters: (id, options) => dispatch(actions.fetchPrinters(id, options)),
        fetchGrades: (id, options) => dispatch(actions.fetchGrades(id, options)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(GeneralPresets));
